import { Fragment, useEffect, useRef } from "react"

import type {
  ActionItem,
  AgentStatusLoaderState,
} from "@/components/agents/agent-status-loader"
import { AgentStatusLoader } from "@/components/agents/agent-status-loader"
import { MessageBubble } from "@/components/ask/message-bubble"
import type { ChatMessage } from "@/types"

// IMP-190: an assistant turn that ran tools carries the narration that streamed
// BEFORE the tools (`intro`) and the completed `actions`, so the turn renders
// in order: intro → progress/Done loader → results (the message content). Plain
// turns (and DB-loaded history) leave these undefined and render as one bubble.
export interface TurnMessage extends ChatMessage {
  intro?: string | null
  actions?: ActionItem[]
}

interface MessageListProps {
  messages: TurnMessage[]
  introContent: string | null
  streamingContent: string | null
  isWaiting: boolean
  agentStatus: AgentStatusLoaderState
  agentActions: ActionItem[]
  onAction?: (message: string) => void
}

// A lightweight assistant bubble for streamed/derived narration (no attachments,
// no action buttons) — used for the intro segment and the live stream.
function narrationMessage(uid: string, content: string): ChatMessage {
  return {
    uid,
    role: "assistant",
    content,
    model_id: null,
    input_tokens: null,
    output_tokens: null,
    created_at: new Date().toISOString(),
  }
}

export function MessageList({
  messages,
  introContent,
  streamingContent,
  isWaiting,
  agentStatus,
  agentActions,
  onAction,
}: MessageListProps) {
  const bottomRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" })
  }, [messages, streamingContent, introContent, agentActions])

  return (
    <div className="flex-1 overflow-y-auto px-4 pt-8">
      <div className="mx-auto w-full max-w-2xl space-y-6">
        {messages.map((msg, idx) => {
          const isLastAssistantMessage =
            msg.role === "assistant" &&
            !messages.slice(idx + 1).some((m) => m.role === "user")
          const hasActions = (msg.actions?.length ?? 0) > 0
          return (
            <Fragment key={msg.uid}>
              {msg.intro && (
                <MessageBubble
                  message={narrationMessage(`${msg.uid}-intro`, msg.intro)}
                />
              )}
              {hasActions && (
                <AgentStatusLoader state="done" actions={msg.actions ?? []} />
              )}
              {msg.content && (
                <MessageBubble
                  message={msg}
                  onAction={isLastAssistantMessage ? onAction : undefined}
                />
              )}
            </Fragment>
          )
        })}

        {/* In-flight turn: the same intro → loader → results ordering, built from
            the live stream. The loader sits between the pre-tool narration and
            the results, never below them. */}
        {introContent && (
          <MessageBubble
            message={narrationMessage("streaming-intro", introContent)}
          />
        )}

        {((isWaiting && streamingContent === null && introContent === null) ||
          agentStatus === "working" ||
          (agentStatus === "done" && agentActions.length > 0)) && (
          <AgentStatusLoader state={agentStatus} actions={agentActions} />
        )}

        {streamingContent !== null && (
          <MessageBubble
            message={narrationMessage("streaming", streamingContent)}
          />
        )}

        <div ref={bottomRef} />
      </div>
    </div>
  )
}
